import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Building2, Briefcase, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, capabilities, industries, insights, stats, locations } from '../mockData';
import Newsletter from '../components/Newsletter';
import Testimonials from '../components/Testimonials';
import Divider from '../components/Divider';

const Home = () => {
  const iconMap = {
    FileText,
    Building2,
    Briefcase,
    Shield
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-black via-[#1a1a1a] to-black">
        {/* Subtle background pattern - no text overlap */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#C9A55A] rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#C9A55A] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16 sm:py-24">
          <h1 className="luxury-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-6 sm:mb-8 leading-tight px-4">
            International Strategic Procurement
            <span className="text-[#C9A55A] block mt-2"> & Elite Advisory Services</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-4 sm:mb-6 max-w-4xl mx-auto leading-relaxed px-4">
            Syndicate Consultants Inc operates at the intersection of global procurement, government advisory, luxury sourcing, and advanced cybersecurity.
          </p>
          <p className="text-base sm:text-lg md:text-xl text-gray-400 mb-8 sm:mb-12 max-w-4xl mx-auto leading-relaxed px-4">
            We empower ministries, sovereign entities, and private-sector leaders with solutions that move seamlessly across borders — discreetly, professionally, and with absolute precision.
          </p>
          
          {/* Locations */}
          <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4 mb-8 sm:mb-12 text-[#C9A55A] text-xs sm:text-sm md:text-base font-medium px-4">
            {locations.map((location, index) => (
              <React.Fragment key={location}>
                <span>{location}</span>
                {index < locations.length - 1 && <span className="text-gray-600">•</span>}
              </React.Fragment>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-4">
            <Button 
              asChild
              className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-base sm:text-lg px-8 sm:px-12 py-4 sm:py-6 smooth-transition shadow-xl"
            >
              <Link to="/contact">
                Get Started <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-[#C9A55A] text-[#C9A55A] hover:bg-[#C9A55A] hover:text-black font-semibold text-base sm:text-lg px-8 sm:px-12 py-4 sm:py-6 smooth-transition"
            >
              <Link to="/services">
                Our Services
              </Link>
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-12 mt-16 sm:mt-24 md:mt-32 px-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="luxury-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#C9A55A] mb-2 sm:mb-3 smooth-transition group-hover:scale-110">{stat.value}</div>
                <div className="text-gray-500 text-xs sm:text-sm tracking-wider uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spacer */}
      <div className="h-20 bg-black"></div>

      {/* Global Capabilities Snapshot */}
      <section className="relative py-16 sm:py-24 md:py-32 overflow-hidden bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6">Global Capabilities Snapshot</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed px-4">
              Comprehensive solutions across government, technology, luxury, and development sectors
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {capabilities.map((capability) => (
              <Card key={capability.id} className="luxury-card bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 hover:border-[#C9A55A]/30 group cursor-pointer">
                <CardContent className="p-6 sm:p-8 md:p-10">
                  <h3 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4 luxury-heading">{capability.title}</h3>
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed">{capability.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* Why Syndicate */}
      <section className="relative py-16 sm:py-24 md:py-32 overflow-hidden bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-6 sm:mb-8">Why Syndicate</h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed mb-4 sm:mb-6">
            Because you need a partner who understands government urgency, private-sector efficiency, and luxury-market precision.
          </p>
          <p className="text-base sm:text-lg md:text-xl text-[#C9A55A] leading-relaxed italic">
            We move quietly, act decisively, and deliver more than expected — every time.
          </p>
        </div>
      </section>

      <Divider />

      {/* Industries Section */}
      <section className="py-16 sm:py-24 md:py-32 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6">Industries We Serve</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed px-4">
              Deep expertise across multiple sectors
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {industries.map((industry) => (
              <Link key={industry.id} to="/industries" className="group">
                <div className="relative overflow-hidden rounded-lg aspect-[4/3] border border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
                  <img 
                    src={industry.image} 
                    alt={industry.name}
                    className="w-full h-full object-cover group-hover:scale-110 smooth-transition duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1 sm:mb-2 luxury-heading">{industry.name}</h3>
                    <p className="text-gray-300 text-xs sm:text-sm">{industry.description}</p>
                  </div>
                  <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-[#C9A55A]/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 smooth-transition border border-[#C9A55A]/30">
                    <ArrowRight className="text-[#C9A55A]" size={16} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* Insights Section */}
      <section className="py-16 sm:py-24 md:py-32 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12 sm:mb-16 md:mb-20 gap-4">
            <div>
              <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-3 sm:mb-6">Latest Insights</h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-400">Expert analysis and industry trends</p>
            </div>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-[#C9A55A] text-[#C9A55A] hover:bg-[#C9A55A] hover:text-black font-semibold smooth-transition text-sm sm:text-base"
            >
              <Link to="/insights">
                View All <ArrowRight className="ml-2" size={16} />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {insights.map((insight) => (
              <Link key={insight.id} to="/insights" className="group">
                <Card className="luxury-card bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 hover:border-[#C9A55A]/30 overflow-hidden">
                  <div className="relative h-40 sm:h-48 overflow-hidden">
                    <img 
                      src={insight.image} 
                      alt={insight.title}
                      className="w-full h-full object-cover group-hover:scale-110 smooth-transition duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  </div>
                  <CardContent className="p-4 sm:p-6">
                    <div className="text-[#C9A55A] text-xs font-semibold mb-2 sm:mb-3 tracking-wider uppercase">{insight.category}</div>
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3 line-clamp-2 group-hover:text-[#C9A55A] smooth-transition">{insight.title}</h3>
                    <p className="text-gray-500 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2 leading-relaxed">{insight.excerpt}</p>
                    <div className="flex justify-between items-center text-xs text-gray-600 pt-3 sm:pt-4 border-t border-white/10">
                      <span>{insight.date}</span>
                      <span>{insight.readTime}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* Testimonials */}
      <Testimonials />

      <Divider />

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};

export default Home;